import { computed, ref } from "vue";

/**
 * Instance-scoped selection state for a review table's per-row approval
 * checkboxes. Each call returns fresh state: `reset(keys)` establishes the
 * selectable universe (deduplicated — several rows may share one selection
 * key — and all keys start selected), and only keys from the latest reset
 * participate — toggling an unknown key is a no-op.
 *
 * Plain refs, no Pinia: instantiate inside a store (or component) and expose
 * the pieces consumers need.
 */
export function useRowSelection() {
  // The keys passed to the latest reset(), in order; selectedKeys preserves it.
  const keys = ref([]);
  // Held in a ref and replaced (not mutated) on every change so dependents
  // re-evaluate without deep Set reactivity.
  const selected = ref(new Set());

  const selectedKeys = computed(() => keys.value.filter((key) => selected.value.has(key)));
  const selectedCount = computed(() => selectedKeys.value.length);
  const allSelected = computed(() => keys.value.length > 0 && selectedCount.value === keys.value.length);
  const noneSelected = computed(() => selectedCount.value === 0);
  const indeterminate = computed(() => !noneSelected.value && !allSelected.value);

  function reset(nextKeys = []) {
    // Deduplicated so selectedKeys counts each key once.
    keys.value = [...new Set(nextKeys)];
    selected.value = new Set(nextKeys);
  }

  function toggle(key) {
    if (!keys.value.includes(key)) {
      return;
    }

    const next = new Set(selected.value);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    selected.value = next;
  }

  function toggleAll() {
    if (allSelected.value) {
      selected.value = new Set();
    } else {
      selected.value = new Set(keys.value);
    }
  }

  function isSelected(key) {
    return selected.value.has(key);
  }

  return {
    reset,
    toggle,
    toggleAll,
    isSelected,
    selectedKeys,
    allSelected,
    noneSelected,
    indeterminate,
    selectedCount,
  };
}
